/**
 * The editing panel: entries, bands and categories, plus the lane overrides.
 *
 * It owns no document state — every action produces a new document and hands it
 * to the caller, which is what makes undo/redo a matter of keeping snapshots.
 */

import {
  addBand,
  addCategory,
  addItem,
  categoryId,
  moveBand,
  pinItemLane,
  removeBand,
  removeCategory,
  removeItem,
  unpinItemLane,
  updateBand,
  updateCategory,
  updateItem,
} from '../../model/mutations';
import type { Band, CategoryId, ChronoDocument, Item } from '../../model/types';
import { createBandPanel } from './band-panel';
import { createCategoryPanel } from './category-panel';
import { createEntryForm } from './entry-form';
import { h } from './dom';

export interface EditorHandlers {
  /** Applied through the document state, which records it for undo. */
  onChange(next: ChronoDocument): void;
  /**
   * The selection changed without the document changing — closing the dialog,
   * for instance. The chart has to be redrawn or the old bar stays highlighted.
   */
  onSelectionChange(): void;
}

export function createEditor(handlers: EditorHandlers) {
  let doc: ChronoDocument | null = null;
  let laneOf: (index: number) => number | null = () => null;

  let selectedItem: number | null = null;
  let selectedBand: number | null = null;
  let selectedCategory: CategoryId | null = null;

  const refresh = () => {
    if (doc) render(doc, laneOf);
  };

  const apply = (next: ChronoDocument) => {
    doc = next;
    handlers.onChange(next);
  };

  const entryForm = createEntryForm({
    onSubmit(item: Item, index: number | null) {
      if (!doc) return;
      if (index === null) {
        apply(addItem(doc, item));
      } else {
        apply(updateItem(doc, index, item));
        selectedItem = index;
      }
    },
    onDelete(index: number) {
      if (!doc) return;
      selectedItem = null;
      apply(removeItem(doc, index));
    },
    onLaneNudge(index: number, delta: number) {
      if (!doc) return;
      const current = laneOf(index);
      if (current === null) return;
      apply(pinItemLane(doc, index, current + delta));
    },
    onLaneReset(index: number) {
      if (!doc) return;
      apply(unpinItemLane(doc, index));
    },
    onCancel() {
      selectedItem = null;
      refresh();
      handlers.onSelectionChange();
    },
  });

  const openEntryDialog = (index: number | null) => {
    selectedItem = index;
    refresh();
    entryForm.open();
  };

  const bandPanel = createBandPanel({
    onSubmit(band: Band, index: number | null) {
      if (!doc) return;
      // Before `apply`, never after: it renders synchronously, and a selection
      // cleared afterwards would only take effect on the next render — leaving
      // the panel in edit mode over a change it had already saved.
      selectedBand = null;
      apply(index === null ? addBand(doc, band) : updateBand(doc, index, band));
    },
    onDelete(index: number) {
      if (!doc) return;
      selectedBand = null;
      apply(removeBand(doc, index));
    },
    onMove(index: number, delta: number) {
      if (!doc) return;
      apply(moveBand(doc, index, delta));
    },
    onSelect(index: number | null) {
      selectedBand = index;
      refresh();
    },
  });

  const categoryPanel = createCategoryPanel({
    onSubmit(name: string, color: string, id: CategoryId | null) {
      if (!doc) return;
      selectedCategory = null;
      apply(
        id === null
          ? addCategory(doc, categoryId(name, doc.categories), { name, color })
          : updateCategory(doc, id, { name, color }),
      );
    },
    onDelete(id: CategoryId) {
      if (!doc) return;
      selectedCategory = null;
      apply(removeCategory(doc, id));
    },
    onSelect(id: CategoryId | null) {
      selectedCategory = id;
      refresh();
    },
  });

  const el = h('div', { class: 'editor' }, entryForm.el, bandPanel.el, categoryPanel.el);

  function render(next: ChronoDocument, lanes: (index: number) => number | null): void {
    doc = next;
    laneOf = lanes;

    if (selectedItem !== null && selectedItem >= next.items.length) selectedItem = null;
    if (selectedBand !== null && selectedBand >= next.bands.length) selectedBand = null;

    entryForm.setCategories(next.categories);

    const item = selectedItem === null ? null : next.items[selectedItem];
    entryForm.select(
      item && selectedItem !== null
        ? { index: selectedItem, item, lane: laneOf(selectedItem) ?? 0 }
        : null,
    );

    bandPanel.setBands(next.bands, selectedBand);
    categoryPanel.setCategories(next, selectedCategory);
  }

  return {
    el,
    render,

    /** From a click in the chart: opens the dialog over the band being read. */
    selectItem(index: number | null): void {
      if (index === null) {
        selectedItem = null;
        entryForm.close();
        refresh();
        return;
      }
      openEntryDialog(index);
    },

    openNewEntry(): void {
      openEntryDialog(null);
    },

    selectedItemIndex(): number | null {
      return selectedItem;
    },
  };
}

export type Editor = ReturnType<typeof createEditor>;
